import React, { createContext, useContext, useState, useCallback } from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'

export interface ToastData {
  id: string
  title: string
  description?: string
  variant: 'success' | 'error' | 'loading' | 'info'
  duration?: number
}

interface ToastContextType {
  toasts: ToastData[]
  addToast: (toast: Omit<ToastData, 'id'>) => string
  removeToast: (id: string) => void
  updateToast: (id: string, updates: Partial<Omit<ToastData, 'id'>>) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export const useToastContext = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToastContext must be used within ToastProvider')
  }
  return context
}

interface ToastProviderProps {
  children: React.ReactNode
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastData[]>([])

  const addToast = useCallback((toast: Omit<ToastData, 'id'>) => {
    // Generate secure ID with fallback
    const id = crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

    // Sanitize input
    const sanitizedToast: ToastData = {
      id,
      title: toast.title?.toString().substring(0, 200) || 'Notification',
      description: toast.description?.toString().substring(0, 500),
      variant: toast.variant || 'info',
      duration: toast.duration ?? (toast.variant === 'error' ? 5000 : 3000)
    }

    setToasts(prev => {
      // Prevent duplicate toasts - check if similar toast exists
      const isDuplicate = prev.some(existingToast =>
        existingToast.title === sanitizedToast.title &&
        existingToast.description === sanitizedToast.description &&
        existingToast.variant === sanitizedToast.variant
      )

      if (isDuplicate) {
        return prev
      }

      const newToasts = [...prev, sanitizedToast]
      // Limit max toasts to prevent memory issues and infinite loops
      return newToasts.slice(-5)
    })
    return id
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }, [])

  const updateToast = useCallback((id: string, updates: Partial<Omit<ToastData, 'id'>>) => {
    setToasts(prev => 
      prev.map(toast => 
        toast.id === id ? { ...toast, ...updates } : toast
      )
    )
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, updateToast }}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        <ToastPrimitive.Viewport className="fixed bottom-0 right-0 flex flex-col p-6 gap-2 w-96 max-w-full m-0 list-none z-50 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  )
}